import React, { ReactElement, useEffect, useState } from "react"
import { Field, Form, Formik } from "formik"
import { Loader } from "semantic-ui-react"
import { toast, ToastOptions } from "react-toastify"
import { useAppApolloClient } from "services/graphql/apollo-client"
import * as userQueries from "services/graphql/queries/user.queries"
import { ClassicInput } from "modules/Carriers/components/ClassicInput"
import { PageWrapper } from "modules/Carriers/components/Layout/Structure"
import PageTitle from "modules/Carriers/components/Title/Title"
import { poppinsOrderSchema } from "schema/poppinsOrder.schema"
import { FormGrid, FormSection, SectionTitle, SubmitButton } from "./PoppinsOrder.styled"

const toastConfig: ToastOptions = {
  icon: false,
  hideProgressBar: true,
  progressStyle: { opacity: 0 },
  style: { animationFillMode: "forwards", animationDuration: "450ms" },
}

export const PoppinsOrder = (): ReactElement => {
  const apolloClient = useAppApolloClient()
  const [companyName, setCompanyName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const initialValues = {
    keeperEmail: "",
    description: "",
    rentalEndDate: "",
    recipient: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
    sender: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  }

  useEffect(() => {
    apolloClient
      .query({ query: userQueries.getCurrentCompanyUser })
      .then(response => {
        setCompanyName(response?.data?.getCurrentCompanyUser?.company?.name ?? null)
      })
      .catch(() => {
        setCompanyName(null)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  if (loading) return <Loader active />

  if (companyName !== "Poppins") {
    return (
      <PageWrapper>
        <PageTitle>Poppins - Créer une commande</PageTitle>
        <p>Accès non autorisé.</p>
      </PageWrapper>
    )
  }

  const onSubmit = async (values: typeof initialValues, { resetForm }: { resetForm: () => void }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/poppins/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `ApiKey POPPINS`,
        },
        body: JSON.stringify(values),
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      toast.success("Commande créée avec succès", { ...toastConfig })
      resetForm()
    } catch {
      toast.error("Quelque chose s'est mal passé.", { ...toastConfig })
    }
  }

  return (
    <PageWrapper>
      <PageTitle>Poppins - Créer une commande</PageTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={poppinsOrderSchema}
        onSubmit={onSubmit}
        validateOnChange
        validateOnBlur={false}
      >
        {({ isValid, isSubmitting }) => (
          <Form>
            <FormSection>
              <SectionTitle>Objet emprunté</SectionTitle>
              <FormGrid>
                <Field
                  type="email"
                  name="keeperEmail"
                  label="Keeper email"
                  placeholder="Keeper email"
                  required
                  component={ClassicInput}
                />
<Field
                  type="text"
                  name="description"
                  label="Description"
                  placeholder="Description"
                  component={ClassicInput}
                />
                <Field
                  type="date"
                  name="rentalEndDate"
                  label="Fin de location"
                  placeholder="Fin de location"
                  component={ClassicInput}
                />
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Borrower</SectionTitle>
              <FormGrid>
                <Field
                  type="text"
                  name="recipient.firstName"
                  label="Prénom"
                  placeholder="Prénom"
                  required
                  component={ClassicInput}
                />
                <Field
                  type="text"
                  name="recipient.lastName"
                  label="Nom"
                  placeholder="Nom"
                  required
                  component={ClassicInput}
                />
                <Field
                  type="email"
                  name="recipient.email"
                  label="Email"
                  placeholder="Email"
                  required
                  component={ClassicInput}
                />
                <Field
                  type="text"
                  name="recipient.phone"
                  label="Téléphone"
                  placeholder="Téléphone"
                  component={ClassicInput}
                />
              </FormGrid>
            </FormSection>

            <FormSection>
              <SectionTitle>Provider</SectionTitle>
              <FormGrid>
                <Field
                  type="text"
                  name="sender.firstName"
                  label="Prénom"
                  placeholder="Prénom"
                  required
                  component={ClassicInput}
                />
                <Field
                  type="text"
                  name="sender.lastName"
                  label="Nom"
                  placeholder="Nom"
                  required
                  component={ClassicInput}
                />
                <Field
                  type="email"
                  name="sender.email"
                  label="Email"
                  placeholder="Email"
                  required
                  component={ClassicInput}
                />
                <Field
                  type="text"
                  name="sender.phone"
                  label="Téléphone"
                  placeholder="Téléphone"
                  component={ClassicInput}
                />
              </FormGrid>
            </FormSection>

            <SubmitButton type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting ? "Envoi en cours..." : "Envoyer"}
            </SubmitButton>
          </Form>
        )}
      </Formik>
    </PageWrapper>
  )
}
